import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
// motion kept for accordion expand/collapse animation only
import { Chip } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import ContactModal from '../../components/feature/ContactModal';
import PageCloudHero from '../../components/feature/PageCloudHero';
import PageNavbar from '../../components/feature/PageNavbar';
import CTA from '../home/components/CTA';
import Footer from '../home/components/Footer';

export default function PricingPage() {
  const { t } = useTranslation();
  const [activeFaq, setActiveFaq] = useState<number | null>(0);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const pricingCards = (t('pricing.cards', { returnObjects: true }) as Array<{
    title: string;
    description: string;
    price: string;
    points: string[];
    note?: string;
  }>).map((card, index) => ({
    ...card,
    icon: [
      'ri-window-line',
      'ri-layout-4-line',
      'ri-shopping-bag-3-line',
      'ri-settings-4-line',
    ][index] ?? 'ri-price-tag-3-line',
    featured: index === 1,
  }));
  const pricingFactors = (t('pricing.factors.items', { returnObjects: true }) as string[]).map(
    (label, index) => ({
      label,
      icon: [
        'ri-ruler-line',
        'ri-pages-line',
        'ri-code-s-slash-line',
        'ri-brush-line',
        'ri-links-line',
        'ri-file-text-line',
      ][index] ?? 'ri-checkbox-circle-line',
    })
  );
  const estimationSteps = t('pricing.estimation.items', { returnObjects: true }) as Array<{
    number: string;
    title: string;
    description: string;
  }>;
  const faqs = t('pricing.faq.items', { returnObjects: true }) as Array<{
    question: string;
    answer: string;
  }>;

  const openInquiryModal = (service: string) => {
    setSelectedService(service);
    setIsContactModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <PageNavbar />

      <PageCloudHero
        badge={t('pricing.hero.badge')}
        badgeIcon="ri-price-tag-3-line"
        title={t('pricing.hero.title')}
        lead={t('pricing.hero.lead')}
        supporting={t('pricing.hero.supporting')}
      />

      <main className="relative z-20 -mt-12 pb-24 md:-mt-24">
        <div className="mx-auto max-w-7xl px-6">
          <section className="mx-auto grid max-w-7xl gap-8 md:gap-9 lg:grid-cols-2 xl:grid-cols-3">
            {pricingCards.map((card, index) => {
              const isFullWidthCard = index === 3;

              return (
              <article
                key={card.title}
                className={`relative overflow-hidden rounded-[32px] border p-7 shadow-[0_24px_64px_rgba(0,0,0,0.28)] backdrop-blur-2xl md:p-8 ${
                  card.featured
                    ? 'border-transparent bg-[linear-gradient(180deg,rgba(26,26,26,0.95)_0%,rgba(26,26,26,0.82)_100%)] shadow-[inset_0_0_0_1px_rgba(163,230,53,0.16),0_24px_64px_rgba(0,0,0,0.28)]'
                    : 'border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.92)_0%,rgba(26,26,26,0.78)_100%)]'
                } ${isFullWidthCard ? 'xl:col-span-3' : 'md:min-h-[430px]'}`}
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(163,230,53,0.12),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(48,77,129,0.14),transparent_34%)]" />

                <div
                  className={`relative z-10 flex h-full flex-col ${
                    isFullWidthCard ? 'xl:grid xl:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.88fr)] xl:gap-10' : ''
                  }`}
                >
                  <div>
                    <div className="flex items-start justify-between gap-4">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.06] text-brand-green shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                        <i className={`${card.icon} text-xl`} />
                      </span>
                      {card.featured ? (
                        <Chip className="border border-brand-green/20 bg-brand-green/[0.09] px-3 py-1 text-xs font-semibold text-brand-green">
                          {t('pricing.featured')}
                        </Chip>
                      ) : null}
                    </div>

                    <div className={`mt-8 ${isFullWidthCard ? 'xl:max-w-2xl' : ''}`}>
                      <p className="text-4xl font-caprasimo leading-none text-white">{card.price}</p>
                      <h2 className="mt-5 text-[1.65rem] font-caprasimo leading-tight text-white md:text-[1.85rem]">
                        {card.title}
                      </h2>
                      <p className="mt-3 max-w-[42ch] text-sm leading-7 text-white/62 md:text-[0.96rem]">
                        {card.description}
                      </p>
                    </div>
                  </div>

                  <div
                    className={`mt-8 flex flex-1 flex-col ${isFullWidthCard ? 'xl:mt-1 xl:justify-between' : ''}`}
                  >
                    <div className={`space-y-4 ${isFullWidthCard ? 'xl:max-w-[430px]' : ''}`}>
                      {card.points.slice(0, 2).map((point) => (
                        <div
                          key={point}
                          className="flex items-start gap-3 text-sm leading-7 text-white/70 md:text-[0.95rem]"
                        >
                          <span className="mt-2.5 inline-flex h-2.5 w-2.5 flex-shrink-0 rounded-full bg-brand-green/80" />
                          <span>{point}</span>
                        </div>
                      ))}
                    </div>

                    <div className={`mt-auto pt-8 ${isFullWidthCard ? 'xl:max-w-[420px] xl:pt-10' : ''}`}>
                      {card.note ? (
                        <p className="mb-5 max-w-[42ch] text-xs leading-6 text-white/48 md:text-[0.84rem]">
                          {card.note}
                        </p>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => openInquiryModal(card.title)}
                        className="inline-flex w-full items-center justify-between rounded-[22px] border border-[#33421b] bg-[linear-gradient(180deg,rgba(163,230,53,0.22)_0%,rgba(163,230,53,0.14)_100%)] px-6 py-4 text-left text-sm font-semibold text-[#eef7cf] shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_16px_30px_rgba(163,230,53,0.1)] backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-[#415824] hover:bg-[linear-gradient(180deg,rgba(163,230,53,0.26)_0%,rgba(163,230,53,0.18)_100%)] hover:text-white hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_20px_36px_rgba(163,230,53,0.14)]"
                      >
                        <span>{t('pricing.ask')}</span>
                        <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#415824] bg-brand-green/18 text-brand-green shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                          <i className="ri-arrow-right-up-line text-base" />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
            })}
          </section>

          <section className="mt-28 grid gap-10 lg:grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)] xl:mt-32">
            <div className="rounded-[30px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.92)_0%,rgba(26,26,26,0.78)_100%)] p-8 shadow-[0_20px_56px_rgba(0,0,0,0.28)] md:p-9">
              <Chip className="border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-brand-green">
                {t('pricing.factors.badge')}
              </Chip>
              <h2 className="mt-6 text-4xl font-caprasimo leading-tight text-white md:text-5xl">
                {t('pricing.factors.title')}
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-8 text-white/64 md:text-base">
                {t('pricing.factors.description')}
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {pricingFactors.map((factor, index) => (
                <div
                  key={factor.label}
                  className="rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.9)_0%,rgba(26,26,26,0.74)_100%)] p-6"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.06] text-brand-green">
                    <i className={`${factor.icon} text-lg`} />
                  </span>
                  <p className="mt-4 text-sm font-semibold leading-7 text-white/84">{factor.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-28 xl:mt-32">
            <div className="mx-auto max-w-3xl text-center">
              <Chip className="border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-brand-green">
                {t('pricing.estimation.badge')}
              </Chip>
              <h2 className="mt-6 text-4xl font-caprasimo leading-tight text-white md:text-5xl">
                {t('pricing.estimation.title')}
              </h2>
            </div>

            <div className="mt-12 grid gap-7 lg:grid-cols-3">
              {estimationSteps.map((step, index) => (
                <article
                  key={step.title}
                  className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.92)_0%,rgba(26,26,26,0.76)_100%)] p-8"
                >
                  <div className="inline-flex rounded-full border border-brand-green/20 bg-brand-green/[0.08] px-4 py-2 text-xs font-semibold tracking-[0.18em] text-brand-green">
                    {step.number}
                  </div>
                  <h3 className="mt-5 text-2xl font-caprasimo leading-tight text-white">{step.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-white/64">{step.description}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-28 xl:mt-32">
            <div className="mx-auto max-w-3xl text-center">
              <Chip className="border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-brand-green">
                {t('pricing.faq.badge')}
              </Chip>
              <h2 className="mt-6 text-4xl font-caprasimo leading-tight text-white md:text-5xl">
                {t('pricing.faq.title')}
              </h2>
            </div>

            <div className="mx-auto mt-12 max-w-4xl space-y-5">
              {faqs.map((item, index) => {
                const isOpen = activeFaq === index;

                return (
                  <div
                    key={item.question}
                    className="overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.92)_0%,rgba(26,26,26,0.78)_100%)]"
                  >
                    <button
                      type="button"
                      onClick={() => setActiveFaq(isOpen ? null : index)}
                      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                    >
                      <span className="text-base font-semibold text-white md:text-lg">
                        {item.question}
                      </span>
                      <span
                        className={`inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/72 transition-transform duration-300 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      >
                        <i className="ri-arrow-down-s-line text-lg" />
                      </span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen ? (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          className="overflow-hidden"
                        >
                          <div className="border-t border-white/8 px-6 pb-6 pt-4 text-sm leading-7 text-white/64 md:text-base">
                            {item.answer}
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </section>

        </div>
      </main>

      <CTA />
      <Footer />

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        presetService={selectedService}
      />
    </div>
  );
}
